/**
 * `phone` — `PhoneValue` (`specs/M2-tier2-column-types.md` §5.8, `[09§A.7 phone]`, `[cvs§2 phone]`).
 *
 * `value` is `{"phone":"…","countryShortName":"IT","changed_at":…}` — camelCase on write, and
 * `country_short_name` on read `[cvs§4.3]`. `text` is the digits exactly as stored, including
 * the leading `+` when there is one; an empty cell stores `{"phone":"","countryShortName":null}`
 * on this account (three such rows) and renders `""` — so emptiness is tested on `phone`, never
 * on the object `[cvs§4.5]`.
 *
 * monday validates with libphonenumber `[09§A.7 phone]`; we do **not** ship a 300 kB validator
 * for 114 populated cells. The stored digits are kept verbatim (`+`, spaces stripped) and the
 * country is stored as the ISO-3166 alpha-2 the caller sent. Recorded as an open question
 * (q331): a number monday would have rejected is accepted here.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const phoneValueSchema = z.looseObject({
    phone: z.string(),
    countryShortName: z.string().nullable().optional(),
    changed_at: z.string().nullable().optional(),
});
export const phoneSettingsSchema = z.looseObject({
    show_flag: z.boolean().optional(),
    hide_footer: z.boolean().optional(),
});
import { phoneFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
const DIGITS_RE = /^\+?[0-9]+$/;
/** `change_simple_column_value`: `"+12025550169 US"` `[09§A.7 phone]`. */
function fromSimple(raw) {
    const trimmed = raw.trim();
    if (trimmed === '')
        return null;
    const [phone, country] = trimmed.split(/\s+/, 2);
    return { phone: phone ?? '', countryShortName: country ?? null };
}
function normalize(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string')
        return fromSimple(raw);
    if (typeof raw === 'number')
        return { phone: String(raw), countryShortName: null };
    if (typeof raw !== 'object') {
        throw new ColumnValueError('phone', `expected an object or string, received ${typeof raw}`);
    }
    const record = raw;
    const rawPhone = record.phone;
    if (rawPhone === null || rawPhone === undefined)
        return null;
    const phone = String(rawPhone).replace(/\s+/g, '');
    if (phone === '')
        return null;
    if (!DIGITS_RE.test(phone)) {
        throw new ColumnValueError('phone', `"${String(rawPhone)}" is not a phone number`);
    }
    // Read spelling `country_short_name`, write spelling `countryShortName` `[cvs§4.3]`.
    const country = record.countryShortName ?? record.country_short_name ?? null;
    const value = {
        phone,
        countryShortName: typeof country === 'string' && country !== '' ? country.toUpperCase() : null,
    };
    // monday ships `"changed_at": null` on many cells; keeping the key (null included) is
    // what makes a re-export byte-identical `[cvs§4.2]`.
    if (keepVolatile && 'changed_at' in record)
        value.changed_at = record.changed_at;
    return value;
}
export const phoneColumn = defineColumnType({
    type: 'phone',
    title: 'Phone',
    description: 'A phone number with its country.',
    apiFragmentName: 'PhoneValue',
    legacyTypeName: 'phone',
    idPrefix: 'phone',
    valueSchema: phoneValueSchema,
    settingsSchema: phoneSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: phoneSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    filterConditions: phoneFilterConditions,
    filterOperators: operatorsOf(phoneFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        return normalize(raw, false);
    },
    parseSimpleValue(raw) {
        return raw === null ? null : fromSimple(raw);
    },
    toText(value) {
        return value?.phone ?? '';
    },
    toSortKeys(value) {
        return { text: textShadow(value?.phone ?? null), number: null, date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return a.phone === b.phone && (a.countryShortName ?? null) === (b.countryShortName ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { phone: value.phone, countryShortName: value.countryShortName ?? null };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { phone: value.phone, countryShortName: value.countryShortName ?? null };
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue(value) {
        if (value === null)
            return '';
        return value.countryShortName ? `${value.phone} ${value.countryShortName}` : value.phone;
    },
    fromMondaySettings(raw) {
        return phoneSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        return {
            phone: value.phone,
            country_short_name: value.countryShortName ?? null,
            updated_at: toFragmentTimestamp(value.changed_at),
        };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    bucketsOf(value) {
        return value?.phone ? [{ key: value.phone, label: value.phone }] : [];
    },
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map